// Parser del Excel "Reparto_*.xlsx" del cliente Dimexa (M12). Toma un
// File del input y devuelve filas tipadas listas para mandar al endpoint
// POST /api/last-mile/import.
//
// La función es pura (acepta un ArrayBuffer, devuelve un Result tagged
// union) para que sea testeable sin DOM. El componente UI se encarga
// del File → ArrayBuffer.

import * as XLSX from "xlsx";

// ---------- Tipos ----------

/**
 * Shape que espera el backend (snake_case wire). Espejo de
 * `lastMileImportRowDTO` en engine/internal/api/lastmile_import.go.
 */
export type RepartoRow = {
  operacion: string;
  doc_type: string;
  series: string;
  number: string;
  condition: string;
  motivo: string;
  valor_venta: number;
  importe: number;
  importe_devolucion: number | null;

  emission_date: string;
  registry_at: string;
  print_at: string;
  dispatch_date: string;
  registry_dispatch_at: string;
  delivered_at: string;
  returned_at: string;

  seller_name: string;
  seller_phone: string;
  seller_user: string;
  seller_area: string;
  supervisor_name: string;

  estado: string;
  motivo_devolucion: string;
  responsable: string;

  cod_cliente: string;
  cliente: string;
  nombre_comercial: string;
  departamento: string;
  provincia: string;
  distrito: string;
  id_establecimiento: string;
  punto_llegada: string;
  lat: number | null;
  lon: number | null;

  sede: string;
  almacen: string;
  division: string;
  canal: string;
  zona: string;

  transportista: string;
  placa: string;
  secuencia: string;

  // Campos de ITEM (DIVISION SALUD). En SALUD una factura puede tener
  // varias filas (1 por producto), todas con misma serie+numero. El
  // backend agrupa por (series, number) y crea N order_items.
  // En CONSUMO estos campos no vienen — quedan en "" / 0.
  id_proveedor: string;
  proveedor: string;
  id_linea: string;
  linea: string;
  cod_producto: string;
  producto: string;
  cantidad: number;
  valor_unitario: number;
  subtotal: number;
  importe_total: number;
};

export type ParseError =
  | { kind: "no-sheet" }
  | { kind: "no-data" }
  | { kind: "missing-headers"; missing: string[] };

export type ParseResult =
  | { ok: true; rows: RepartoRow[]; warnings: string[] }
  | { ok: false; error: ParseError };

// ---------- Mapeo header → key del DTO ----------
//
// Las claves del Excel se normalizan (trim + uppercase) antes del lookup
// para tolerar variaciones de formato. El backend ya hace trim/upper de
// sede/almacén/placa también — acá nos enfocamos en mapeo de columnas.

const COL_MAP: Record<string, keyof RepartoRow> = {
  "OPERACION": "operacion",
  "DOCUMENTO": "doc_type",
  "SERIE": "series",
  "NUMERO": "number",
  "CONDICION": "condition",
  "MOTIVO": "motivo",
  "VALOR VENTA": "valor_venta",
  // Typo del Excel fuente ("DOCUENTO" en vez de "DOCUMENTO"). Lo
  // soportamos por compat — ojo si en algún momento corrigen el header.
  "IMPORTE DOCUENTO": "importe",
  "IMPORTE DOCUMENTO": "importe",
  "IMPORTE DEVOLUCION": "importe_devolucion",
  "F. EMISION": "emission_date",
  "F. REGISTRO": "registry_at",
  "F. IMPRESION": "print_at",
  "F. DESPACHO": "dispatch_date",
  "F. REGISTRO DESPACHO": "registry_dispatch_at",
  "F. DE ENTREGA CL.": "delivered_at",
  "F. DEVOLUCION": "returned_at",
  "VENDEDOR": "seller_name",
  // Header alternativo — el Excel piloto usa "NOMBRE VENDEDOR" (más
  // explícito) mientras que otros exports usan "VENDEDOR". Aceptamos
  // ambos.
  "NOMBRE VENDEDOR": "seller_name",
  "TELEFONO VENDEDOR": "seller_phone",
  "TELÉFONO VENDEDOR": "seller_phone",
  "USUARIO": "seller_user",
  "AREA USUARIO": "seller_area",
  // SUPERVISOR viene en minúsculas en el Excel.
  "SUPERVISOR": "supervisor_name",
  "SITUACION / ESTADO": "estado",
  "MOTIVO DEVOLUCION": "motivo_devolucion",
  "RESPONSABLE": "responsable",
  "COD. CLIENTE": "cod_cliente",
  "CLIENTE": "cliente",
  "NOMBRE COMERCIAL": "nombre_comercial",
  "DEPARTAMENTO": "departamento",
  "PROVINCIA": "provincia",
  "DISTRITO": "distrito",
  "IDESTABLECIMIENTO": "id_establecimiento",
  "PUNTO LLEGADA": "punto_llegada",
  "LATITUD": "lat",
  "LONGITUD": "lon",
  "SEDE": "sede",
  "ALMACEN": "almacen",
  "DIVISION": "division",
  "CANAL": "canal",
  "ZONA": "zona",
  "TRANSPORTISTA": "transportista",
  "PLACA": "placa",
  "SECUENCIA": "secuencia",

  // Columnas de ITEM (Excel SALUD).
  "ID PROVEEDOR": "id_proveedor",
  "PROVEEDOR": "proveedor",
  "IDLINEA": "id_linea",
  "LINEA": "linea",
  "COD PRODUCTO": "cod_producto",
  "PRODUCTO": "producto",
  "CANTIDAD": "cantidad",
  "VAL. UNI": "valor_unitario",
  "SUBTOTAL": "subtotal",
  // OJO: "IMPORTE TOTAL" del item vs "IMPORTE DOCUMENTO" del header (CONSUMO).
  // Acá `importe_total` es por línea (cantidad × val_uni); `importe` sigue
  // siendo el total del documento (CONSUMO).
  "IMPORTE TOTAL": "importe_total",
};

// Las columnas mínimas para que un Excel sea procesable. El resto puede
// faltar; en su lugar el backend pondrá NULL.
//
// PLACA no es required: la derivamos de los últimos 7 chars de TRANSPORTISTA
// si la columna no viene (formato típico peruano: "EMPRESA SA …-BWO-871"
// → placa "BWO-871"). Ver completeDefaults().
const REQUIRED_COLS = [
  "DOCUMENTO",
  "SERIE",
  "NUMERO",
  "COD. CLIENTE",
  "CLIENTE",
  "F. DESPACHO",
  "TRANSPORTISTA",
] as const;

const NUMERIC_KEYS: Set<keyof RepartoRow> = new Set([
  "valor_venta", "importe", "importe_devolucion", "lat", "lon",
  "cantidad", "valor_unitario", "subtotal", "importe_total",
]);
const DATE_KEYS: Set<keyof RepartoRow> = new Set([
  "emission_date", "registry_at", "print_at", "dispatch_date",
  "registry_dispatch_at", "delivered_at", "returned_at",
]);

// ---------- Parsing ----------

/** Parsea un ArrayBuffer (de FileReader o fetch) del Excel. */
export function parseRepartoExcel(buf: ArrayBuffer): ParseResult {
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) {
    return { ok: false, error: { kind: "no-sheet" } };
  }
  const sheet = wb.Sheets[sheetName];

  // header=1 → devuelve matriz de arrays, ideal para encontrar headers
  // tolerando filas vacías al inicio. defval:"" pone string vacío en celdas vacías.
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: "",
    raw: false, // strings para todo (los dates vienen formateados)
  });

  if (matrix.length === 0) {
    return { ok: false, error: { kind: "no-data" } };
  }

  // Encontrar la fila de headers — primer row que tenga ≥4 columnas conocidas.
  // En Numbers/Excel a veces hay filas decorativas al inicio.
  let headerRowIdx = -1;
  let mapping: (keyof RepartoRow | null)[] = [];
  for (let i = 0; i < Math.min(5, matrix.length); i++) {
    const row = matrix[i] ?? [];
    const m = row.map((cell) => mapHeader(String(cell ?? "")));
    const known = m.filter((x) => x !== null).length;
    if (known >= 4) {
      headerRowIdx = i;
      mapping = m;
      break;
    }
  }
  if (headerRowIdx < 0) {
    return { ok: false, error: { kind: "missing-headers", missing: [...REQUIRED_COLS] } };
  }

  // Verificar columnas requeridas.
  const presentKeys = new Set(mapping.filter((x): x is keyof RepartoRow => x !== null));
  const missing = REQUIRED_COLS.filter((col) => {
    const expected = COL_MAP[col];
    return !expected || !presentKeys.has(expected);
  });
  if (missing.length > 0) {
    return { ok: false, error: { kind: "missing-headers", missing } };
  }

  // Necesitamos los raw values con cellDates=true para fechas y números
  // genuinos. Re-leemos solo el rango de datos como objects con header=mapping.
  const sheetRaw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    range: headerRowIdx,
    defval: null,
    raw: true,
  });

  const rows: RepartoRow[] = [];
  const warnings: string[] = [];

  for (let i = 0; i < sheetRaw.length; i++) {
    const raw = sheetRaw[i];
    if (!raw || Object.values(raw).every((v) => v === null || v === "")) continue;

    const out: Partial<RepartoRow> = {};
    for (const [excelCol, mappedKey] of Object.entries(COL_MAP)) {
      const value = lookupValue(raw, excelCol);
      // COL_MAP puede tener múltiples aliases que mapean a la misma key
      // destino (ej. "IMPORTE DOCUENTO" y "IMPORTE DOCUMENTO" → `importe`).
      // Si una alias ya escribió un valor real, no la pisamos con null
      // del alias que no aparece en este Excel.
      const alreadySet = (out as Record<string, unknown>)[mappedKey];
      const incoming = value;
      if (alreadySet !== undefined && alreadySet !== null && alreadySet !== "" &&
          (incoming === null || incoming === undefined || incoming === "")) {
        continue;
      }
      assignField(out, mappedKey, incoming, warnings, i);
    }

    // Si tras el mapping no quedó ni el cod_cliente, ni placa, ni
    // transportista (de donde podríamos derivar la placa), la fila no
    // sirve para el flujo de import. La skipeamos silenciosamente.
    if (!out.cod_cliente && !out.placa && !out.transportista) continue;

    rows.push(completeDefaults(out));
  }

  if (rows.length === 0) {
    return { ok: false, error: { kind: "no-data" } };
  }
  return { ok: true, rows, warnings };
}

// ---------- Helpers ----------

function mapHeader(raw: string): keyof RepartoRow | null {
  const norm = raw.trim().toUpperCase();
  return COL_MAP[norm] ?? null;
}

function lookupValue(raw: Record<string, unknown>, excelCol: string): unknown {
  // sheet_to_json mantiene la capitalización original del header. Buscamos
  // case-insensitive iterando las claves disponibles.
  const target = excelCol.toUpperCase();
  for (const [k, v] of Object.entries(raw)) {
    if (k.trim().toUpperCase() === target) return v;
  }
  return null;
}

function assignField(
  out: Partial<RepartoRow>,
  key: keyof RepartoRow,
  raw: unknown,
  warnings: string[],
  rowIdx: number,
) {
  if (NUMERIC_KEYS.has(key)) {
    if (raw === null || raw === undefined || raw === "") {
      (out as Record<string, unknown>)[key] = key === "importe_devolucion" || key === "lat" || key === "lon" ? null : 0;
      return;
    }
    const n = typeof raw === "number" ? raw : parseFloat(String(raw).replace(",", "."));
    if (Number.isFinite(n)) {
      (out as Record<string, unknown>)[key] = n;
    } else {
      (out as Record<string, unknown>)[key] = key === "importe_devolucion" || key === "lat" || key === "lon" ? null : 0;
      warnings.push(`fila ${rowIdx + 2}: ${key} inválido (${raw})`);
    }
    return;
  }
  if (DATE_KEYS.has(key)) {
    if (raw instanceof Date) {
      (out as Record<string, unknown>)[key] = formatISO(raw);
    } else if (typeof raw === "string" && raw.trim() !== "") {
      // Intentamos parsear; si no, mandamos el string y el backend hace fallback.
      const d = new Date(raw);
      (out as Record<string, unknown>)[key] = Number.isNaN(d.getTime()) ? raw : formatISO(d);
    } else {
      (out as Record<string, unknown>)[key] = "";
    }
    return;
  }
  // String genérico — trim. Conservamos el string aún si tiene espacios.
  (out as Record<string, unknown>)[key] = raw == null ? "" : String(raw).trim();
}

/// Deriva la placa a partir de TRANSPORTISTA cuando la columna PLACA no
/// vino en el Excel o quedó vacía. Convención del ERP del cliente:
///   "RAZON SOCIAL DEL TRANSPORTISTA …-BWO-871"
/// Los últimos 7 caracteres son el formato peruano `XXX-NNN`.
function derivePlacaFromTransportista(transportista: string): string {
  const t = transportista.trim();
  if (t.length < 7) return "";
  return t.slice(-7).toUpperCase();
}

function completeDefaults(partial: Partial<RepartoRow>): RepartoRow {
  return {
    operacion: partial.operacion ?? "",
    doc_type: partial.doc_type ?? "",
    series: partial.series ?? "",
    number: partial.number ?? "",
    condition: partial.condition ?? "",
    motivo: partial.motivo ?? "",
    valor_venta: partial.valor_venta ?? 0,
    importe: partial.importe ?? 0,
    importe_devolucion: partial.importe_devolucion ?? null,
    emission_date: partial.emission_date ?? "",
    registry_at: partial.registry_at ?? "",
    print_at: partial.print_at ?? "",
    dispatch_date: partial.dispatch_date ?? "",
    registry_dispatch_at: partial.registry_dispatch_at ?? "",
    delivered_at: partial.delivered_at ?? "",
    returned_at: partial.returned_at ?? "",
    seller_name: partial.seller_name ?? "",
    seller_phone: partial.seller_phone ?? "",
    seller_user: partial.seller_user ?? "",
    seller_area: partial.seller_area ?? "",
    supervisor_name: partial.supervisor_name ?? "",
    estado: partial.estado ?? "",
    motivo_devolucion: partial.motivo_devolucion ?? "",
    responsable: partial.responsable ?? "",
    cod_cliente: partial.cod_cliente ?? "",
    cliente: partial.cliente ?? "",
    nombre_comercial: partial.nombre_comercial ?? "",
    departamento: partial.departamento ?? "",
    provincia: partial.provincia ?? "",
    distrito: partial.distrito ?? "",
    id_establecimiento: partial.id_establecimiento ?? "",
    punto_llegada: partial.punto_llegada ?? "",
    lat: partial.lat ?? null,
    lon: partial.lon ?? null,
    sede: partial.sede ?? "",
    almacen: partial.almacen ?? "",
    division: partial.division ?? "",
    canal: partial.canal ?? "",
    zona: partial.zona ?? "",
    transportista: partial.transportista ?? "",
    placa: (partial.placa && partial.placa.trim() !== "")
      ? partial.placa
      : derivePlacaFromTransportista(partial.transportista ?? ""),
    secuencia: partial.secuencia ?? "",

    id_proveedor: partial.id_proveedor ?? "",
    proveedor: partial.proveedor ?? "",
    id_linea: partial.id_linea ?? "",
    linea: partial.linea ?? "",
    cod_producto: partial.cod_producto ?? "",
    producto: partial.producto ?? "",
    cantidad: partial.cantidad ?? 0,
    valor_unitario: partial.valor_unitario ?? 0,
    subtotal: partial.subtotal ?? 0,
    importe_total: partial.importe_total ?? 0,
  };
}

function formatISO(d: Date): string {
  // ISO sin zona, formato que el backend acepta vía parseTime layouts.
  return d.toISOString().slice(0, 19);
}

// ---------- Stats para preview ----------

export type RepartoStats = {
  totalRows: number;
  totalDocs: number;
  uniqueClients: number;
  uniqueDrivers: number;
  uniqueVehicles: number;
  dispatchDates: { date: string; count: number }[];
  drivers: { placa: string; transportista: string; docs: number; clients: number }[];
  estadosCount: { estado: string; count: number }[];
  rowsWithoutCoords: number;
};

export function statsFor(rows: RepartoRow[]): RepartoStats {
  const docs = new Set<string>();
  const clients = new Set<string>();
  const drivers = new Map<string, { transportista: string; docs: Set<string>; clients: Set<string> }>();
  const vehicles = new Set<string>();
  const dispatchDates = new Map<string, number>();
  const estados = new Map<string, number>();
  let noCoords = 0;

  for (const r of rows) {
    docs.add(`${r.series}-${r.number}`);
    if (r.cod_cliente) clients.add(r.cod_cliente);
    if (r.placa) vehicles.add(r.placa);

    if (r.placa) {
      const e = drivers.get(r.placa) ?? {
        transportista: r.transportista,
        docs: new Set<string>(),
        clients: new Set<string>(),
      };
      e.docs.add(`${r.series}-${r.number}`);
      if (r.cod_cliente) e.clients.add(r.cod_cliente);
      drivers.set(r.placa, e);
    }

    const date = r.dispatch_date ? r.dispatch_date.slice(0, 10) : "(sin fecha)";
    dispatchDates.set(date, (dispatchDates.get(date) ?? 0) + 1);

    const estado = r.estado || "(sin estado)";
    estados.set(estado, (estados.get(estado) ?? 0) + 1);

    if (r.lat == null || r.lon == null) noCoords++;
  }

  return {
    totalRows: rows.length,
    totalDocs: docs.size,
    uniqueClients: clients.size,
    uniqueDrivers: drivers.size,
    uniqueVehicles: vehicles.size,
    dispatchDates: Array.from(dispatchDates.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => b.count - a.count),
    drivers: Array.from(drivers.entries())
      .map(([placa, e]) => ({
        placa,
        transportista: e.transportista,
        docs: e.docs.size,
        clients: e.clients.size,
      }))
      .sort((a, b) => b.docs - a.docs),
    estadosCount: Array.from(estados.entries())
      .map(([estado, count]) => ({ estado, count }))
      .sort((a, b) => b.count - a.count),
    rowsWithoutCoords: noCoords,
  };
}
